import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { FolderKanban, MessageSquare, Building2, FileText, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [pwDialog, setPwDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers }).then(r => setStats(r.data)).catch(() => {});
  }, [token]);

  const handlePasswordChange = async () => {
    if (pwForm.new_password.length < 10) { toast.error('Mindestens 10 Zeichen'); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwörter stimmen nicht überein'); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: pwForm.current_password, new_password: pwForm.new_password }, { headers });
      toast.success('Passwort geändert');
      setPwDialog(false);
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
    setPwLoading(false);
  };

  const cards = [
    { title: 'Projekte', value: stats.projects || 0, icon: FolderKanban, color: 'text-[#066ce7] bg-blue-50' },
    { title: 'Kontaktanfragen', value: stats.contacts || 0, sub: `${stats.unread_contacts || 0} ungelesen`, icon: MessageSquare, color: 'text-emerald-600 bg-emerald-50' },
    { title: 'Hallenanfragen', value: stats.hall_inquiries || 0, sub: `${stats.new_inquiries || 0} neu`, icon: Building2, color: 'text-amber-600 bg-amber-50' },
    { title: 'Blog-Beitrage', value: stats.blog_posts || 0, icon: FileText, color: 'text-violet-600 bg-violet-50' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-[#0F172A] mb-8">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, i) => (
          <Card key={i} className="border-slate-200" data-testid={`dashboard-card-${i}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">{card.title}</CardTitle>
              <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center`}>
                <card.icon className="w-5 h-5" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#0F172A]">{card.value}</div>
              {card.sub && <p className="text-xs text-slate-500 mt-1">{card.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6 flex items-center justify-between" data-testid="admin-info-box">
        <div>
          <h3 className="font-bold text-[#0F172A] mb-1">Sicherheit</h3>
          <p className="text-sm text-slate-500">Passwort regelmässig ändern für mehr Sicherheit.</p>
        </div>
        <Button onClick={() => setPwDialog(true)} variant="outline" className="gap-2" data-testid="change-password-btn">
          <KeyRound className="w-4 h-4" /> Passwort ändern
        </Button>
      </div>

      <Dialog open={pwDialog} onOpenChange={setPwDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Passwort ändern</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Aktuelles Passwort</Label>
              <Input type="password" value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-xs">Neues Passwort</Label>
              <Input type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-xs">Neues Passwort bestätigen</Label>
              <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialog(false)}>Abbrechen</Button>
            <Button onClick={handlePasswordChange} disabled={pwLoading} className="bg-[#066ce7] text-white" data-testid="save-password-btn">
              {pwLoading ? '...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
